import { Observable, firstValueFrom } from 'rxjs';
import { Injectable } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { environment } from './../../environments/environment';
import { EnvironmentalEffectDto } from '../data-transfer/entities/environmental-effect-dto';
import { CountryDto } from '../data-transfer/entities/country-dto'
import { LCAAnalysisType } from '../data-transfer/entities/environmental-effect-authorization-dto';
import { AnalysisType } from '../navigation/services/navigation-services/parent-navigation-service';
@Injectable({ providedIn: 'root' })
export class LicenseService {

  private _isCradleToGateAllowed: boolean;
  private _isCradleToGraveAllowed: boolean;
  private _isCCAllowed: boolean;
  private _isPPWRAllowed: boolean;
  private _isExcelDownloadAllowed: boolean;
  private _allowedEffects: Map<LCAAnalysisType,EnvironmentalEffectDto[]>;
  private _allowedCountries: CountryDto[];

  constructor(
    private http: HttpClient,
  ) {
    this._isCradleToGateAllowed = false;
    this._isCradleToGraveAllowed = false;
    this._isCCAllowed = false;
    this._isPPWRAllowed = false;
    this._isExcelDownloadAllowed = false;
    this._allowedEffects = new Map<LCAAnalysisType,EnvironmentalEffectDto[]>();
    this._allowedCountries = [];
  }

  get isCradleToGateAllowed() : boolean {
    return this._isCradleToGateAllowed
  }

  get isCradleToGraveAllowed() : boolean {
    return this._isCradleToGraveAllowed
  }

  get isCCAllowed() : boolean {
    return this._isCCAllowed
  }

  get isPPWRAllowed() : boolean {
    return this._isPPWRAllowed
  }

  get isExcelDownloadAllowed(): boolean {
    return this._isExcelDownloadAllowed
  }

  get allowedEffects() : Map<LCAAnalysisType,EnvironmentalEffectDto[]> {
    return this._allowedEffects
  }

  get allowedCountries() : CountryDto[] {
    return this._allowedCountries
  }

  public async refreshingLicenses() {
      await this.getCradleToGateAllowed();
      await this.getCradleToGraveAllowed();
      await this.getAuthorizedEnvironmentalEffects(LCAAnalysisType.CradleToGrave);
      await this.getAuthorizedEnvironmentalEffects(LCAAnalysisType.CradleToGate);
      await this.getCCAllowed();
      await this.getExcelDownloadAllowed();
      await this.getAuthorizedCountries();
      await this.getPPWRAllowed();
  }

  public async getAuthorizedEnvironmentalEffectsFromTimeStampAsync(timestamp : string, analysisType: LCAAnalysisType) : Promise<EnvironmentalEffectDto[]> {
    let params: HttpParams = new HttpParams();
    params = params.set('timestamp', timestamp);
    params = params.set('analysisType', analysisType);
    const url =  `${environment.webApiAddress}/api/licensing/authorized-environmental-effects`;
    return firstValueFrom(this.http.get<EnvironmentalEffectDto[]>(url, { params }));
  }

  public async getAuthorizedCountriesFromTimeStampAsync(timestamp : string) : Promise<CountryDto[]> {
    let params: HttpParams = new HttpParams();
    params = params.set('timestamp', timestamp);
    const url =  `${environment.webApiAddress}/api/licensing/authorized-countries`;
    return firstValueFrom(this.http.get<CountryDto[]>(url, { params }));
  }

  private async getCradleToGateAllowed() {
    let params: HttpParams = new HttpParams();
    params = params.set('analysisType', LCAAnalysisType.CradleToGate);
    const url = `${environment.webApiAddress}/api/licensing/is-lca-analysis-allowed`;
    const result = await firstValueFrom(this.http.get<boolean>(url, { params }));
    if (result !== undefined) {
      this._isCradleToGateAllowed = result;
    }
  }

  private async getCradleToGraveAllowed() {
    let params: HttpParams = new HttpParams();
    params = params.set('analysisType', LCAAnalysisType.CradleToGrave);
    const url = `${environment.webApiAddress}/api/licensing/is-lca-analysis-allowed`;
    const result = await firstValueFrom(this.http.get<boolean>(url, { params }));
    if (result !== undefined) {
      this._isCradleToGraveAllowed = result;
    }
  }

  private async getCCAllowed() {
    const url = `${environment.webApiAddress}/api/licensing/is-cost-calculation-allowed`;
    const result =  await firstValueFrom(this.http.get<boolean>(url));
    if (result !== undefined) {
      this._isCCAllowed = result;
    }
  }

  private async getPPWRAllowed() {
    const url = `${environment.webApiAddress}/api/licensing/is-ppwr-allowed`;
    const result =  await firstValueFrom(this.http.get<boolean>(url));
    if (result !== undefined) {
      this._isPPWRAllowed = result;
    }
  }

  private async getExcelDownloadAllowed() {
    const url = `${environment.webApiAddress}/api/licensing/is-excel-download-allowed`;
    const result = await firstValueFrom(this.http.get<boolean>(url));
    if (result !== undefined) {
      this._isExcelDownloadAllowed = result;
    }
  }

  private async getAuthorizedCountries() {
    const url = `${environment.webApiAddress}/api/licensing/authorized-countries`;
    const result = await firstValueFrom(this.http.get<CountryDto[]>(url));
    if (result !== undefined) {
      this._allowedCountries = result;
    }
  }

  private async getAuthorizedEnvironmentalEffects(analysisType: LCAAnalysisType) {
    const url = `${environment.webApiAddress}/api/licensing/authorized-environmental-effects`;
    let params: HttpParams = new HttpParams();
    params = params.set('analysisType', analysisType);
    const result = await firstValueFrom(this.http.get<EnvironmentalEffectDto[]>(url,{ params }));
    if (result !== undefined) {
        this._allowedEffects.set(analysisType,result);
    }
  }

}
